import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';

import { useFirmUserList } from 'hooks/useFirmUserList';
import { generateFullName } from 'utils/generateFullNameUtils';
import { handleRedirection } from 'utils/utils';
import { handleSettingSkAndPkToLocalStorage } from 'utils/localStorageUtils';
import ImageComponent from 'shared/ImageComponent';
import GlobalTooltip from 'components/GlobalTooltip';

export default function TeamMember({ selectedList, setSelectedList, leadDetails }) {
  const history = useHistory();
  const { userList } = useFirmUserList(true, true);

  const usersOnly = userList?.filter((user) => user?.res_type === 'user');

  useEffect(() => {
    if (!userList || userList.length === 0) return;

    if (selectedList && selectedList.length > 0 && selectedList.some((item) => item?.type)) {
      return;
    }

    const existingTeamMembers = selectedList?.filter((member) => member?.firm_user_id || member?.user_group_id) || [];

    const modifiedExistingMembers = existingTeamMembers.map((member) => ({
      ...member,
      type: member?.user_group_id ? 'user-group' : 'user',
    }));

    const existingUserIds = new Set(existingTeamMembers.map((m) => m?.firm_user_id).filter(Boolean));
    const existingGroupIds = new Set(existingTeamMembers.map((m) => m?.user_group_id).filter(Boolean));

    const newUsers = userList
      ?.filter((user) => user?.res_type !== 'user-group' && !existingUserIds.has(user?.firm_user_id))
      ?.map((user) => ({
        ...user,
        type: 'user',
      }));

    const newGroups = userList
      ?.filter((group) => group?.res_type === 'user-group' && !existingGroupIds.has(group?.user_group_id))
      ?.map((group) => ({
        ...group,
        type: 'user-group',
      }));

    const leadAssignee = leadDetails?.lead_assign_to?.[0];
    if (leadAssignee && !existingUserIds.has(leadAssignee?.firm_user_id)) {
      const leadAssigneeUser = userList?.find((user) => user?.firm_user_id === leadAssignee?.firm_user_id);
      if (leadAssigneeUser) {
        const leadAssigneeIndex = newUsers?.findIndex((user) => user?.firm_user_id === leadAssignee?.firm_user_id);
        if (leadAssigneeIndex !== -1) {
          newUsers[leadAssigneeIndex] = {
            ...newUsers[leadAssigneeIndex],
            ...leadAssignee,
            is_selected: true,
            derived_from_group: false,
          };
        }
      }
    }

    const allItems = [...modifiedExistingMembers, ...(newUsers || []), ...(newGroups || [])];
    setSelectedList(allItems);
  }, [userList, leadDetails?.lead_assign_to]);

  const onChangeHandler = (e) => {
    setSelectedList((preVal) => {
      let updatedList = preVal?.map((val) => {
        if (val?.firm_user_id || val?.user_group_id) {
          if (val?.firm_user_id === e.target.value || val?.user_group_id === e.target.value) {
            if (e.target.name === 'is_selected') {
              if (!e.checked) {
                return {
                  ...val,
                  [e.target.name]: e.checked,
                  is_lead_associate: false,
                  is_lead_attorney: false,
                  is_lead_paralegal: false,
                  is_lead_case_manager: false,
                };
              } else if (e.checked && !e.target.isGroup) {
                return { ...val, [e.target.name]: e.checked, derived_from_group: false };
              } else {
                return { ...val, [e.target.name]: e.checked };
              }
            } else {
              return {
                ...val,
                is_lead_associate: false,
                is_lead_attorney: false,
                is_lead_paralegal: false,
                is_lead_case_manager: false,
                [e.target.name]: true,
                is_selected: true,
                derived_from_group: e.target.isGroup ? undefined : false,
              };
            }
          }
        } else if (e.target.name !== 'is_selected') {
          return { ...val, [e.target.name]: false };
        }
        return { ...val };
      });

      if (e.target.name === 'is_selected') {
        updatedList = updatedList?.map((val) => {
          if (val?.type === 'user') {
            const userGroups = val?.user_group || [];
            const selectedGroups = updatedList?.filter((v) => v?.type === 'user-group' && v?.is_selected) || [];

            const isUserInSelectedGroup = userGroups.some((userGroup) => selectedGroups.some((group) => group?.sk === userGroup?.sk));

            if (e.target.name === 'is_selected' && e.target.isGroup) {
              if (e.checked) {
                const belongsToClickedGroup = userGroups.some((userGroup) =>
                  selectedGroups.some((group) => group?.sk === userGroup?.sk && group?.user_group_id === e.target.value)
                );

                if (belongsToClickedGroup) {
                  const hasRoleAssignment =
                    val?.is_lead_attorney || val?.is_lead_associate || val?.is_lead_paralegal || val?.is_lead_case_manager;

                  if (val?.derived_from_group === false || hasRoleAssignment === true) {
                    return { ...val, is_selected: true, derived_from_group: false };
                  } else {
                    return {
                      ...val,
                      is_selected: true,
                      derived_from_group: true,
                    };
                  }
                }
              } else {
                const wasDerivedFromClickedGroup =
                  val?.derived_from_group === true &&
                  userGroups.some((userGroup) =>
                    selectedGroups.some((group) => group?.sk === userGroup?.sk && group?.user_group_id === e.target.value)
                  );

                if (wasDerivedFromClickedGroup) {
                  const stillInOtherGroups = userGroups.some((userGroup) =>
                    selectedGroups.some((group) => group?.sk === userGroup?.sk && group?.user_group_id !== e.target.value)
                  );

                  if (!stillInOtherGroups) {
                    const hasRoleAssignment =
                      val?.is_lead_attorney || val?.is_lead_associate || val?.is_lead_paralegal || val?.is_lead_case_manager;

                    if (hasRoleAssignment) {
                      return { ...val, is_selected: true, derived_from_group: false };
                    } else {
                      return {
                        ...val,
                        is_selected: false,
                      };
                    }
                  }
                }
              }
            }

            if (isUserInSelectedGroup) {
              const hasRoleAssignment =
                val?.is_lead_attorney || val?.is_lead_associate || val?.is_lead_paralegal || val?.is_lead_case_manager;

              if (val?.derived_from_group === false || hasRoleAssignment) {
                return { ...val, is_selected: true, derived_from_group: false };
              } else {
                return { ...val, is_selected: true, derived_from_group: true };
              }
            } else {
              if (val?.derived_from_group === true) {
                const hasRoleAssignment =
                  val?.is_lead_attorney || val?.is_lead_associate || val?.is_lead_paralegal || val?.is_lead_case_manager;

                if (hasRoleAssignment) {
                  return { ...val, is_selected: true, derived_from_group: false };
                } else {
                  return {
                    ...val,
                    is_selected: false,
                  };
                }
              }
            }
          }
          return val;
        });
      }

      return updatedList;
    });
  };

  const getSelectedItem = (name) => selectedList?.find((v) => v?.[name] === true && !v?.group_name)?.firm_user_id;

  const isUserInSelectedGroup = (rowData) => {
    return (
      rowData?.type === 'user' &&
      selectedList?.some(
        (group) =>
          group?.type === 'user-group' && group?.is_selected && rowData?.user_group?.find((userGroup) => userGroup?.sk === group?.sk)
      )
    );
  };

  const nameTemplate = (rowData) => {
    const { sk, pk, firm_user_id } = rowData;
    let url = '/settings/team-management';
    let stateToPass = {};

    if (firm_user_id) {
      url = firm_user_id ? `/settings/team-management/${firm_user_id}` : '/settings/team-management';
      stateToPass = rowData;
    }

    return (
      <div
        className="d-flex flex-column flex-sm-row w-100"
        onClick={() => {
          handleRedirection(history, url, stateToPass);
          handleSettingSkAndPkToLocalStorage(sk, pk);
        }}
      >
        <span className="p-column-title text-break d-none">Lead Attorney</span>
        <ImageComponent filePath={rowData?.profile_image} fileName={rowData?.first_name} fileSize="small.jpg" className="col-1 me-2" />
        <span className="d-flex align-items-center text-break redirection-link-no-text-transform">
          {rowData?.group_name || generateFullName(rowData)}
        </span>
      </div>
    );
  };

  const selectColumnTemplate = (rowData) => {
    const userInSelectedGroup = isUserInSelectedGroup(rowData);
    const isDisabled = !(rowData.status === 'ACTIVE') || userInSelectedGroup;

    return (
      <GlobalTooltip tooltip={userInSelectedGroup ? 'User is already selected through a group' : ''} position="top">
        <Checkbox
          id="is_selected"
          checked={rowData?.is_selected}
          name="is_selected"
          value={rowData.firm_user_id || rowData.user_group_id}
          onChange={(e) => {
            e.target.isGroup = rowData?.type === 'user-group';
            onChangeHandler(e);
          }}
          disabled={isDisabled}
          style={isDisabled ? { opacity: 0.5 } : undefined}
        />
      </GlobalTooltip>
    );
  };

  const rowheaderTemplate = (data) => {
    return (
      <React.Fragment>
        <strong>{data?.type === 'user-group' ? 'Groups' : 'Users'}</strong>
      </React.Fragment>
    );
  };

  return (
    <Card className="card-bg shadow-middle billing-preferences F-size14">
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-4 p-medium">Lead Attorney</div>
        <div className="col-6">
          <Dropdown
            name="is_lead_attorney"
            optionLabel={(v) => generateFullName(v)}
            optionValue="firm_user_id"
            options={usersOnly}
            onChange={onChangeHandler}
            className="input-shadow w-100"
            value={getSelectedItem('is_lead_attorney')}
            filter
            resetFilterOnHide
          />
        </div>
      </div>

      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-4 p-medium">Lead Associate</div>
        <div className="col-6">
          <Dropdown
            name="is_lead_associate"
            optionLabel={(v) => generateFullName(v)}
            optionValue="firm_user_id"
            options={usersOnly}
            onChange={onChangeHandler}
            className="input-shadow w-100"
            value={getSelectedItem('is_lead_associate')}
            filter
            resetFilterOnHide
          />
        </div>
      </div>
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-4 p-medium">Lead Paralegal</div>
        <div className="col-6">
          <Dropdown
            name="is_lead_paralegal"
            optionLabel={(v) => generateFullName(v)}
            optionValue="firm_user_id"
            options={usersOnly}
            onChange={onChangeHandler}
            className="input-shadow w-100"
            value={getSelectedItem('is_lead_paralegal')}
            filter
            resetFilterOnHide
          />
        </div>
      </div>
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-4 p-medium">Lead Case Manager</div>
        <div className="col-6">
          <Dropdown
            name="is_lead_case_manager"
            optionLabel={(v) => generateFullName(v)}
            optionValue="firm_user_id"
            options={usersOnly}
            onChange={onChangeHandler}
            className="input-shadow w-100"
            value={getSelectedItem('is_lead_case_manager')}
            filter
            resetFilterOnHide
          />
        </div>
      </div>
      <div className="d-flex mb-3">
        <div className="col-12">
          <DataTable
            value={selectedList}
            responsiveLayout="scroll"
            emptyMessage="No Data Found."
            selectionMode="checkbox"
            className="case-team-member"
            groupField="type"
            rowGroupMode="subheader"
            rowGroupHeaderTemplate={rowheaderTemplate}
            rowGroupFooterTemplate={() => <></>}
          >
            <Column body={selectColumnTemplate} style={{ width: 25 }} headerStyle={{ width: 25 }} />
            <Column field="name" header="Name" className="add-contact-name" body={nameTemplate} />
          </DataTable>
        </div>
      </div>
    </Card>
  );
}
